import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { PartySelect } from "@/components/catalog/PartySelect";
import { ProductSelect, type LedgerProductOption } from "@/components/catalog/ProductSelect";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { listPartyOptions, syncPartiesFromApi, type PartyOption } from "@/lib/parties/partiesStore";
import {
  downloadPartyLedgerExcel,
  downloadPartyLedgerPdf,
  fetchPartyLedger,
  fetchPartyLedgerProductOptions,
  type PartyLedger,
  type PartyLedgerTypeFilter,
} from "@/lib/parties/partyLedgerStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { BookOpen, FileDown, FileSpreadsheet, Loader2, RotateCcw, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

function money(amount: number, symbol: string, emptyZero = false): string {
  if (emptyZero && Math.abs(amount) < 0.005) return "";
  const formatted = Math.abs(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol ? `${symbol} ` : ""}${formatted}`;
}

function renderBalance(amount: number, side: "Dr" | "Cr") {
  const val = Math.abs(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (side === "Cr") {
    return <span className="text-rose-600 dark:text-rose-400 font-semibold">({val})</span>;
  }
  return <span>{val}</span>;
}

function formatDate(value: string | null): string {
  if (!value) return "";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

const TYPE_OPTIONS: { value: PartyLedgerTypeFilter; label: string }[] = [
  { value: "all", label: "All parties" },
  { value: "customer", label: "Customer" },
  { value: "supplier", label: "Supplier" },
  { value: "investor", label: "Investor" },
];

function partyMatchesType(party: PartyOption, type: PartyLedgerTypeFilter): boolean {
  if (type === "all") return true;
  const t = (party.type ?? "").toLowerCase();
  return t === type || t === "all" || t === "both";
}

const PartyLedgerPage = () => {
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView =
    can(user, "customer_can_view_any") ||
    can(user, "supplier_can_view_any") ||
    can(user, "investor_can_view_any") ||
    can(user, "chart_of_account_can_view_any");

  const [isForbidden, setIsForbidden] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [hasApplied, setHasApplied] = useState(false);
  const [ledger, setLedger] = useState<PartyLedger | null>(null);
  const [parties, setParties] = useState<PartyOption[]>(() => listPartyOptions());

  const initialType = (() => {
    const raw = (searchParams.get("type") ?? "").toLowerCase();
    return raw === "customer" || raw === "supplier" || raw === "investor" ? raw : "all";
  })();
  const initialPartyId = searchParams.get("party_id")?.trim() || "";

  const [typeFilter, setTypeFilter] = useState<PartyLedgerTypeFilter>(initialType);
  const [partyId, setPartyId] = useState(initialPartyId);
  const [productId, setProductId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [productOptions, setProductOptions] = useState<LedgerProductOption[]>([]);
  const autoAppliedKeyRef = useRef<string | null>(null);
  const [currencyMode, setCurrencyMode] = useState<"base" | "party">("base");

  useEffect(() => {
    setCurrencyMode("base");
  }, [partyId]);

  const currencySymbol = user?.company_branding?.currency?.symbol?.trim() || "";

  const activeFilters = useMemo(
    () => ({
      type: typeFilter,
      partyId: partyId ? Number(partyId) : null,
      productId: productId ? Number(productId) : null,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
    }),
    [typeFilter, partyId, productId, dateFrom, dateTo],
  );

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void syncPartiesFromApi()
      .then(() => {
        if (!cancelled) setParties(listPartyOptions());
      })
      .catch(() => {
        /* keep cached */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchPartyLedgerProductOptions({
      type: typeFilter,
      partyId: partyId ? Number(partyId) : null,
    })
      .then((rows) => {
        if (cancelled) return;
        setProductOptions(rows);
        // Drop selection if it is no longer in the filtered options.
        if (productId && !rows.some((r) => String(r.id) === String(productId))) {
          setProductId("");
        }
      })
      .catch((error) => {
        console.error("[PartyLedger] Product options failed", error);
        if (!cancelled) setProductOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [typeFilter, partyId]);

  const partyOptions = useMemo(
    () => parties.filter((p) => partyMatchesType(p, typeFilter)),
    [parties, typeFilter],
  );

  const applyFilters = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setIsForbidden(false);
    try {
      const data = await fetchPartyLedger(activeFilters);
      setLedger(data);
      setHasApplied(true);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        setIsForbidden(true);
        return;
      }
      console.error("[PartyLedger] Load failed", error);
      toast.error("Could not load party ledger.");
    } finally {
      setLoading(false);
    }
  }, [canView, activeFilters]);

  useEffect(() => {
    if (!canView || !initialPartyId) return;
    const key = `${initialType}|${initialPartyId}`;
    if (autoAppliedKeyRef.current === key) return;
    autoAppliedKeyRef.current = key;
    void applyFilters();
  }, [canView, initialType, initialPartyId, applyFilters]);

  const exportReport = useCallback(
    async (format: "pdf" | "excel") => {
      if (!hasApplied || exporting) return;
      setExporting(format);
      try {
        if (format === "pdf") {
          await downloadPartyLedgerPdf({ ...activeFilters, currencyMode });
          toast.success("PDF downloaded.");
        } else {
          await downloadPartyLedgerExcel({ ...activeFilters, currencyMode });
          toast.success("Excel downloaded.");
        }
      } catch (error) {
        console.error(`[PartyLedger] ${format} export failed`, error);
        toast.error(format === "pdf" ? "Could not download PDF." : "Could not download Excel.");
      } finally {
        setExporting(null);
      }
    },
    [hasApplied, exporting, activeFilters, currencyMode],
  );

  const resetFilters = () => {
    setTypeFilter("all");
    setPartyId("");
    setProductId("");
    setDateFrom("");
    setDateTo("");
    setLedger(null);
    setHasApplied(false);
    setCurrencyMode("base");
  };

  const titleType = TYPE_OPTIONS.find((o) => o.value === typeFilter)?.label ?? "All parties";
  const selectedPartyName = partyId
    ? partyOptions.find((p) => String(p.id) === String(partyId))?.name
    : null;
  const selectedProductName = productId
    ? productOptions.find((p) => String(p.id) === String(productId))?.name
    : null;

  const showCurrencySwitch =
    ledger?.partyCurrency != null &&
    ledger.partyCurrency.code.toLowerCase() !== "pkr" &&
    ledger.partyCurrency.code.toLowerCase() !== currencySymbol.toLowerCase();

  const activeSymbol =
    currencyMode === "party" && ledger?.partyCurrency
      ? ledger.partyCurrency.symbol
      : currencySymbol;

  const activeOpeningBalance =
    currencyMode === "party" && ledger
      ? ledger.totals.partyOpeningBalance
      : ledger?.totals.openingBalance ?? 0;

  const activeOpeningBalanceSide =
    currencyMode === "party" && ledger
      ? ledger.totals.partyOpeningBalanceSide
      : ledger?.totals.openingBalanceSide ?? "Dr";

  const activeTotalDebit =
    currencyMode === "party" && ledger
      ? ledger.totals.partyDebit
      : ledger?.totals.debit ?? 0;

  const activeTotalCredit =
    currencyMode === "party" && ledger
      ? ledger.totals.partyCredit
      : ledger?.totals.credit ?? 0;

  const activeTotalBalance =
    currencyMode === "party" && ledger
      ? ledger.totals.partyBalance
      : ledger?.totals.balance ?? 0;

  const activeTotalBalanceSide =
    currencyMode === "party" && ledger
      ? ledger.totals.partyBalanceSide
      : ledger?.totals.balanceSide ?? "Dr";

  const hasOpening = ledger != null && Math.abs(ledger.totals.openingBalance) >= 0.005;
  const hasRows = (ledger?.lines.length ?? 0) > 0 || hasOpening;

  if (!canView || isForbidden) {
    return (
      <Layout>
        <PermissionDenied />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <h1 className="font-display text-xl font-bold tracking-tight text-foreground">Party Ledger</h1>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Accounting sub-ledger by party type (customer, supplier, investor) from posted journal entries.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-border/55 bg-card/80 p-4 shadow-sm ring-1 ring-border/25">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Party type</Label>
              <Select
                value={typeFilter}
                onValueChange={(v) => {
                  setTypeFilter(v as PartyLedgerTypeFilter);
                  setPartyId("");
                }}
              >
                <SelectTrigger className="glass-input h-9 text-xs">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Party</Label>
              <PartySelect
                value={partyId}
                options={partyOptions}
                onChange={setPartyId}
                placeholder={`All ${typeFilter === "all" ? "parties" : `${typeFilter}s`}`}
                clearable
                clearLabel={`All ${typeFilter === "all" ? "parties" : `${typeFilter}s`}`}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Product</Label>
              <ProductSelect
                value={productId}
                options={productOptions}
                onChange={setProductId}
                placeholder="All products"
                clearable
                clearLabel="All products"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">From date</Label>
              <DatePartsPicker
                value={dateFrom}
                onChange={setDateFrom}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                maxDate={dateTo ? new Date(`${dateTo}T00:00:00`) : undefined}
                placeholder="Select from date"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">To date</Label>
              <DatePartsPicker
                value={dateTo}
                onChange={setDateTo}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                minDate={dateFrom ? new Date(`${dateFrom}T00:00:00`) : undefined}
                placeholder="Select to date"
                align="end"
              />
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              {hasApplied ? (
                <>
                  Showing: <span className="font-semibold text-foreground">{titleType}</span>
                  {selectedPartyName ? (
                    <>
                      {" · "}
                      <span className="font-semibold text-foreground">{selectedPartyName}</span>
                    </>
                  ) : null}
                  {selectedProductName ? (
                    <>
                      {" · "}
                      <span className="font-semibold text-foreground">{selectedProductName}</span>
                    </>
                  ) : null}
                </>
              ) : (
                "Choose filters, then click Apply filter."
              )}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={!hasApplied || loading || exporting != null}
                onClick={() => void exportReport("pdf")}
              >
                {exporting === "pdf" ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FileDown className="mr-1.5 h-3.5 w-3.5" />
                )}
                PDF
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={!hasApplied || loading || exporting != null}
                onClick={() => void exportReport("excel")}
              >
                {exporting === "excel" ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" />
                )}
                Excel
              </Button>
              <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={resetFilters}>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Reset
              </Button>
              <Button
                type="button"
                variant="premium"
                size="sm"
                className="h-8 text-xs"
                onClick={() => void applyFilters()}
                disabled={loading}
              >
                {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Search className="mr-1.5 h-3.5 w-3.5" />}
                Apply filter
              </Button>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-border/55 bg-card/80 shadow-sm ring-1 ring-border/25">
          {loading ? (
            <div className="flex h-48 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : !hasApplied ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No ledger loaded yet. Apply a filter to view entries.
            </p>
          ) : !ledger || !hasRows ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No posted ledger entries for this filter.
            </p>
          ) : (
            <div>
              {showCurrencySwitch ? (
                <div className="flex items-center justify-between border-b border-border/55 bg-muted/20 px-4 py-2.5">
                  <span className="text-xs font-semibold text-muted-foreground">Ledger Currency Mode</span>
                  <div className="flex items-center gap-1.5 rounded-lg bg-background p-0.5 border border-border/60">
                    <button
                      type="button"
                      className={cn(
                        "rounded-md px-3 py-1 text-xs font-medium transition-all",
                        currencyMode === "base"
                          ? "bg-primary text-primary-foreground shadow-xs"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                      onClick={() => setCurrencyMode("base")}
                    >
                      Base ({currencySymbol || "PKR"})
                    </button>
                    <button
                      type="button"
                      className={cn(
                        "rounded-md px-3 py-1 text-xs font-medium transition-all",
                        currencyMode === "party"
                          ? "bg-primary text-primary-foreground shadow-xs"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                      onClick={() => setCurrencyMode("party")}
                    >
                      Supplier ({ledger.partyCurrency?.code})
                    </button>
                  </div>
                </div>
              ) : null}
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date / Voucher #</TableHead>
                      <TableHead>Particulars</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Debit{activeSymbol ? ` (${activeSymbol})` : ""}</TableHead>
                      <TableHead className="text-right">Credit{activeSymbol ? ` (${activeSymbol})` : ""}</TableHead>
                      <TableHead className="text-right">Balance{activeSymbol ? ` (${activeSymbol})` : ""}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow className="bg-muted/30">
                      <TableCell className="font-semibold text-foreground" colSpan={3}>
                        Opening Balance
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground" />
                      <TableCell className="text-right tabular-nums text-muted-foreground" />
                      <TableCell className="text-right tabular-nums font-semibold">
                        {renderBalance(activeOpeningBalance, activeOpeningBalanceSide)}
                      </TableCell>
                    </TableRow>
                    {ledger.lines.map((line) => {
                      const lineDebit = currencyMode === "party" ? line.partyDebit : line.debit;
                      const lineCredit = currencyMode === "party" ? line.partyCredit : line.credit;
                      const lineBalance = currencyMode === "party" ? line.partyBalance : line.balance;
                      const lineBalanceSide = currencyMode === "party" ? line.partyBalanceSide : line.balanceSide;

                      return (
                        <TableRow key={line.id}>
                          <TableCell className="min-w-[8rem] whitespace-nowrap">
                            <div className="text-sm text-foreground">{formatDate(line.date)}</div>
                            <div className="font-mono text-[11px] text-muted-foreground">{line.voucher || ""}</div>
                          </TableCell>
                          <TableCell className="min-w-[10rem] max-w-[18rem]">
                            <div className="space-y-0.5">
                              {line.particulars.map((text, idx) => (
                                <p
                                  key={`${line.id}-p-${idx}`}
                                  className={cn(
                                    "leading-snug",
                                    idx === 0
                                      ? "text-xs font-medium text-foreground"
                                      : "text-[11px] text-muted-foreground",
                                  )}
                                >
                                  {text}
                                </p>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="min-w-[8rem] max-w-[14rem]">
                            <span className="text-xs text-muted-foreground leading-snug">
                              {line.productName || ""}
                            </span>
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                            {money(lineDebit, "", true)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-rose-700 dark:text-rose-400">
                            {money(lineCredit, "", true)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-medium">
                            {renderBalance(lineBalance, lineBalanceSide)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={3} className="font-semibold">
                        Closing Balance
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold text-emerald-700 dark:text-emerald-400">
                        {money(activeTotalDebit, "")}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold text-rose-700 dark:text-rose-400">
                        {money(activeTotalCredit, "")}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">
                        {renderBalance(activeTotalBalance, activeTotalBalanceSide)}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PartyLedgerPage;
